import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  ArrowRight,
  Sparkles,
  Users,
  Gamepad2,
  Star,
  Zap,
} from 'lucide-react';
import { gsap } from 'gsap';
import DarkVeilBackground from '../components/DarkVeilBackground';
import { useGSAP } from '../hooks/useGSAP';
import { useTranslation } from '../i18n';
import LegalLinks from '../components/LegalLinks';
import { HeaderBrand, Logo, MascotPair } from '../components/brand';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  const { staggerIn, slideIn, scaleIn, fadeIn, createTimeline } = useGSAP();

  useEffect(() => {
    if (!containerRef.current) return;

    // Main timeline for entrance animations
    const tl = createTimeline();

    // Animate logo
    if (logoRef.current) {
      tl.fromTo(
        logoRef.current,
        {
          scale: 0.6,
          opacity: 0,
        },
        {
          scale: 1,
          opacity: 1,
          duration: 0.8,
          ease: 'back.out(1.7)',
        }
      );
    }

    // Animate title with typewriter effect
    if (titleRef.current) {
      tl.fromTo(
        titleRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
        },
        '-=0.5'
      );
    }

    // Animate subtitle
    if (subtitleRef.current) {
      tl.fromTo(
        subtitleRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '-=0.4'
      );
    }

    // Animate CTA buttons
    if (ctaRef.current) {
      tl.fromTo(
        ctaRef.current.children,
        {
          opacity: 0,
          scale: 0.8,
          y: 20,
        },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.2,
          ease: 'back.out(1.7)',
        },
        '-=0.3'
      );
    }

    // Animate features with stagger
    if (featuresRef.current) {
      tl.fromTo(
        featuresRef.current.children,
        {
          opacity: 0,
          y: 50,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
        },
        '-=0.2'
      );
    }

    // Continuous floating animation for the hero content
    if (heroRef.current) {
      gsap.to(heroRef.current, {
        y: -10,
        duration: 3,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }, [createTimeline, staggerIn, slideIn, scaleIn, fadeIn]);

  return (
    <div ref={containerRef} className="min-h-screen relative overflow-hidden">
      {/* Dark Veil Background with Red Hue Shift */}
      <DarkVeilBackground hueShift={true} intensity={0.7} />

      {/* Header */}
      <header className="relative z-20">
        <nav
          data-testid="landing-header"
          className="max-w-7xl mx-auto px-3 min-[375px]:px-4 sm:px-6 lg:px-8 py-4 sm:py-6"
        >
          <div className="flex flex-nowrap items-center justify-between gap-2 sm:gap-4">
            <div ref={logoRef} className="flex min-w-0 shrink items-center">
              <HeaderBrand />
            </div>
            <div className="flex shrink-0 flex-nowrap items-center gap-2 min-[375px]:gap-3 sm:gap-4">
              <Link
                to="/auth/login"
                className="relative whitespace-nowrap text-xs min-[375px]:text-sm sm:text-base text-white/90 after:absolute after:-inset-x-1 after:-inset-y-3 after:content-[''] hover:text-white font-medium transition-colors duration-200 drop-shadow"
              >
                {t('landing.signIn')}
              </Link>
              <Link
                to="/start"
                className="relative whitespace-nowrap bg-hc-accent-strong text-white px-2.5 py-1.5 min-[375px]:px-3 min-[375px]:py-2 sm:px-6 text-xs min-[375px]:text-sm sm:text-base rounded-pill font-display font-extrabold hover:bg-rose-800 transition-all duration-200 active:scale-[0.98] after:absolute after:-inset-1 after:content-['']"
              >
                {t('landing.getStarted')}
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section: a cream bubble with the light lockup (brand surface) */}
      <main className="relative z-10">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <section
            data-surface="cream"
            data-testid="landing-hero"
            className="rounded-bubble bg-hc-cream px-4 py-8 text-hc-ink sm:px-10 sm:py-14"
          >
            <div ref={heroRef} className="text-center">
              <div className="mx-auto mb-6 w-full max-w-[640px] sm:mb-8">
                <Logo
                  variant="lockup"
                  tone="light"
                  height={292}
                  className="mx-auto w-full"
                />
              </div>

              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-hc-ink/10 bg-hc-ink/5 px-5 py-2.5">
                <Sparkles
                  className="h-5 w-5 text-hc-accent-strong"
                  aria-hidden="true"
                />
                <span className="text-sm font-semibold text-hc-ink/80">
                  {t('landing.badge')}
                </span>
                <Star className="h-4 w-4 text-hc-gold" aria-hidden="true" />
              </div>

              <h1
                ref={titleRef}
                className="mb-5 font-display text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl"
              >
                <span>{t('landing.hero.line1')}</span>{' '}
                <span className="block text-hc-accent-strong">
                  {t('landing.hero.line2')}
                </span>
              </h1>

              <p
                ref={subtitleRef}
                className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-hc-ink/80 sm:text-xl"
              >
                {t('landing.hero.subtitle')}
              </p>

              <div
                ref={ctaRef}
                className="flex flex-col items-center justify-center gap-4"
              >
                <Link
                  to="/start"
                  className="group inline-flex min-h-[52px] items-center justify-center rounded-full bg-hc-accent px-8 py-3 font-display text-cta font-extrabold text-hc-cream shadow-glow transition-all duration-200 hover:bg-hc-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-ink focus-visible:ring-offset-2 focus-visible:ring-offset-hc-cream active:scale-[0.98]"
                >
                  <Zap
                    className="me-2 inline-block h-5 w-5"
                    aria-hidden="true"
                  />
                  {t('landing.cta.start')}
                  <ArrowRight
                    className="ms-2 inline-block h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
                    aria-hidden="true"
                  />
                </Link>

                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
                  <Link
                    to="/auth/register"
                    className="inline-flex min-h-[44px] items-center rounded-tile text-lg font-semibold text-hc-ink underline decoration-hc-accent-strong decoration-2 underline-offset-4 transition-colors duration-200 hover:text-hc-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-ink focus-visible:ring-offset-2 focus-visible:ring-offset-hc-cream"
                  >
                    {t('landing.cta.createAccount')}
                  </Link>
                  <Link
                    to="/auth/login"
                    className="inline-flex min-h-[44px] items-center rounded-tile text-lg font-semibold text-hc-ink underline decoration-hc-accent-strong decoration-2 underline-offset-4 transition-colors duration-200 hover:text-hc-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-ink focus-visible:ring-offset-2 focus-visible:ring-offset-hc-cream"
                  >
                    {t('landing.cta.haveAccount')}
                  </Link>
                </div>
                <p className="text-sm font-medium text-hc-ink/70">
                  {t('landing.free.note')}
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <MascotPair size={140} className="mx-auto mb-8 text-hc-cream" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto backdrop-blur-sm bg-white/5 rounded-bubble p-4 border border-white/10">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div
            ref={featuresRef}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {/* Feature 1 */}
            <div className="text-center group">
              <div className="w-20 h-20 bg-hc-accent/15 backdrop-blur-lg rounded-tile flex items-center justify-center mx-auto mb-6 border border-white/20 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <Heart className="h-10 w-10 text-hc-accent-soft transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 drop-shadow">
                {t('landing.feature.bonds.title')}
              </h3>
              <p className="text-white/70 backdrop-blur-sm bg-white/5 rounded-bubble p-4 border border-white/10">
                {t('landing.feature.bonds.body')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center group">
              <div className="w-20 h-20 bg-hc-secondary/15 backdrop-blur-lg rounded-tile flex items-center justify-center mx-auto mb-6 border border-white/20 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <Gamepad2 className="h-10 w-10 text-hc-secondary transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 drop-shadow">
                {t('landing.feature.fun.title')}
              </h3>
              <p className="text-white/70 backdrop-blur-sm bg-white/5 rounded-bubble p-4 border border-white/10">
                {t('landing.feature.fun.body')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center group">
              <div className="w-20 h-20 bg-hc-gold/15 backdrop-blur-lg rounded-tile flex items-center justify-center mx-auto mb-6 border border-white/20 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <Users className="h-10 w-10 text-hc-gold transition-colors" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 drop-shadow">
                {t('landing.feature.shared.title')}
              </h3>
              <p className="text-white/70 backdrop-blur-sm bg-white/5 rounded-bubble p-4 border border-white/10">
                {t('landing.feature.shared.body')}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-black/30 backdrop-blur-lg border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-4 flex items-center justify-center">
            <Logo variant="wordmark" height={48} />
          </div>
          <p className="text-white/80 mb-4 drop-shadow">
            {t('landing.footer.tagline')}
          </p>
          <nav className="mb-4 flex justify-center">
            <Link
              to="/help"
              className="inline-flex min-h-[44px] items-center px-3 text-sm font-semibold text-white/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-accent rounded-tile"
            >
              {t('nav.help')}
            </Link>
          </nav>
          <LegalLinks className="mb-4" />
          <div className="text-sm text-white/60">
            {t('landing.footer.rights', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
